// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@awc/App/Components/Notify";
import xLoadingOverlay from "@awc/App/Handlers/XNG/LoadingOverlay";
import { CustomAppState } from "@awc/App/Store/Reducers";
import TextField from "@awc/MobiusOverrides/TextField";
import { doScroll, xValidateOnSubmit } from "@awc/XDL/Utilities";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import { AccountModel, CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, RefObject, useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    addAccount,
    signIn,
    validatePassword,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
    // XNG-Change
    loadCurrentCountries,
    xLoadingOverlay,
};

const mapStateToProps = (state: CustomAppState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    returnUrl: getCreateAccountReturnUrl(state),
    homePageUrl: getHomePageUrl(state),
    reCaptchaEnabledForCreateAccount: getSettingsCollection(state).websiteSettings.reCaptchaEnabledForCreateAccount,
    // XNG-Change
    countries: getCurrentCountries(state),
    isMobileScreen: state.xCommon.isMobileScreen,
});

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasToasterContext;

export interface CreateAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    activationInstructionGridItem?: GridItemProps;
    activationInstructionText?: TypographyPresentationProps;
    activationSuccessWrapper?: InjectableCss;
    activationSuccessTitleText?: TypographyPresentationProps;
    activationSuccessMessageText?: TypographyPresentationProps;
    activationSuccessHomeButton?: ButtonPresentationProps;
    // XNG-Change
    accountNoTextField?: TextFieldPresentationProps;
    phoneTextField?: TextFieldPresentationProps;
    accountNoGridItem?: GridItemProps;
    phoneGridItem?: GridItemProps;
    existingCustomerGridItem?: GridItemProps;
    sharedGridItem?: GridItemProps;
    sharedTextField?: TextFieldPresentationProps;
    countrySelect?: SelectPresentationProps;
    requiredGridItem?: GridItemProps;
    requiredText?: TypographyProps;
    applyAsExistingCustomerWrapper?: InjectableCss;
    billingTitle?: TypographyPresentationProps;
    requirementsWrapper?: InjectableCss;
}

export const createAccountStyles: CreateAccountStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    userGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 100px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    userGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 300px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    emailGridItem: {
        width: 12,
    },
    userNameGridItem: {
        width: 12,
    },
    passwordGridItem: {
        width: 12,
    },
    confirmPasswordGridItem: {
        width: 12,
    },
    subscriptionGridItem: {
        width: 12,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    requirementsGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 50px;
                        `,
                    ],
                    "min",
                )}/* stylelint-disable-line declaration-block-single-line-max-declarations */
        `,
    },
    passwordRequirementsGridContainer: {
        gap: 5,
    },
    passwordRequirementsGridItem: {
        width: 12,
    },
    requirementsTitle: {
        variant: "h4",
        as: "h2",
    },
    activationInstructionGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 50px;
                        `,
                    ],
                    "min",
                )}/* stylelint-disable-line declaration-block-single-line-max-declarations */
        `,
    },
    activationSuccessWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            width: 50%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    activationSuccessTitleText: {
        variant: "h3",
    },
    activationSuccessHomeButton: {
        css: css`
            align-self: flex-end;
        `,
    },
};

const styles = createAccountStyles;
const StyledForm = getStyledWrapper("form");

type CreateAccountFields = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    userName: string;
    password: string;
    confirmPassword: string;
    accountNumber: string;
    companyName: string;
    phone: string;
    address1: string;
    address2: string;
    postalCode: string;
    country: CountryModel | undefined;
    city: string;
    state: string;
};
const CreateAccount = ({
    addAccount,
    signIn,
    validatePassword,
    validateReCaptcha,
    accountSettings,
    returnUrl,
    history,
    homePageUrl,
    reCaptchaEnabledForCreateAccount,
    // XNG-Change
    loadCurrentCountries,
    countries,
    toaster,
    isMobileScreen,
    xLoadingOverlay,
}: Props) => {
    // XNG-Change
    const initialFormData = {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        userName: "",
        password: "",
        confirmPassword: "",
        accountNumber: "",
        companyName: "",
        phone: "",
        address1: "",
        address2: "",
        city: "",
        postalCode: "",
        country: countries?.[0]?.abbreviation,
        state: countries?.[0]?.states?.[0]?.abbreviation,
    };
    const userNameRequiredFieldMessage = siteMessage("CreateNewAccountInfo_UserName_Required");
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");

    const [email, setEmail] = useState("");
    const [userName, setUserName] = useState("");
    const [userNameError, setUserNameError] = useState(
        accountSettings.useEmailAsUserName ? "" : userNameRequiredFieldMessage,
    );
    const [errorMessage, setErrorMessage] = useState("");
    const [password, setPassword] = useState("");
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<ReactNode>("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState<ReactNode>("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isActivationEmailSent, setIsActivationEmailSent] = useState(false);
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    // XNG-Change
    const [isExistingCustomer, setIsExistingCustomer] = useState(false);
    const [countryErrorMessage, setCountryErrorMessage] = useState("");

    const [formValues, setFormValues] = useState({ ...initialFormData });
    const formId = "create-account-form";
    const [country, setCountry] = useState<CountryModel>();
    const [state, setState] = useState<StateModel>();

    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();
    const requiredFieldErrorMessage = "Country is a required field";
    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
        passwordRequiresLowercase,
        passwordRequiresDigit,
        useEmailAsUserName,
        requireActivateAccount,
    } = accountSettings;

    // XNG-Change
    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    }, []);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = event.target as HTMLInputElement | HTMLSelectElement;
        setFormValues(prevValues => ({
            ...prevValues,
            [name]: value,
        }));

        if (name === "country") {
            const selectedCountry = countries?.find(c => c.abbreviation === value);
            setCountry(selectedCountry);
            setState(undefined);
            setFormValues(prevValues => ({
                ...prevValues,
                state: undefined,
            }));

            value ? setCountryErrorMessage("") : setCountryErrorMessage(requiredFieldErrorMessage);
        }

        if (name === "state") {
            const selectedState = country?.states?.find(state => state.abbreviation === value);
            setState(selectedState);
        }

        if (name === "password" || name === "confirmPassword") {
            validateConfirmPassword(
                name === "password" ? value : formValues.password,
                name === "confirmPassword" ? value : formValues.confirmPassword,
            );

            if (name === "password") {
                validatePassword({
                    password: value,
                    onComplete: errorMessage => {
                        setPasswordErrorMessage(errorMessage);
                    },
                });
            }
        }

        setIsSubmitted(false);
        setIsSubmitting(false);
    };

    const validateUserName = (name: string) => {
        const errorMessage = !name && !useEmailAsUserName ? userNameRequiredFieldMessage : "";
        setUserNameError(errorMessage);
        return !errorMessage;
    };

    const validateConfirmPassword = (newPassword: string, newConfirmPassword: string) => {
        if (!newPassword || !newConfirmPassword) {
            setConfirmPasswordErrorMessage("");
            return true;
        }
        const errorMessage =
            newPassword !== newConfirmPassword
                ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
                : "";
        setConfirmPasswordErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        return !(countryErrorMessage || confirmPasswordErrorMessage || passwordErrorMessage);
    };

    const emailChangeHandler = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : validateEmail(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        setEmail(email);
    };

    const userChangeHandler = (name: string) => {
        validateUserName(name);
        setUserName(name);
    };

    const confirmPasswordChangeHandler = (newConfirmPassword: string) => {
        // XNG-Change
        setFormValues(prevValues => ({
            ...prevValues,
            confirmPassword: newConfirmPassword,
        }));
        validateConfirmPassword(password, newConfirmPassword);
    };

    const onSuccessHandler = (account?: AccountModel) => {
        setIsSubmitting(false);
        xLoadingOverlay({ key: "clear", id: "Create_Account" });
        setErrorMessage("");
        setFormValues({ ...initialFormData });

        // XNG-Change: AWC-212: showing toaster when creating new account
        toasterContext.addToast({
            body: siteMessage("Custom_Create_Account_Success_Site_Message"),
            messageType: "success",
        });

        if (account?.activationStatus === "Activated") {
            setTimeout(() => {
                signIn({
                    userName: useEmailAsUserName ? formValues.email : formValues.userName,
                    password: formValues.password,
                    rememberMe: false,
                    returnUrl,
                });
            }, 500);
        } else {
            setIsActivationEmailSent(true);
        }
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const countryErrorMessage = isExistingCustomer && !formValues.country ? requiredFieldErrorMessage : "";

        setCountryErrorMessage(countryErrorMessage);

        if (xValidateOnSubmit(formId).length > 0) {
            return;
        }
        if (!validateConfirmPassword(formValues.password, formValues.confirmPassword)) {
            doScroll();
            return;
        }
        if (!validateSubmitEnabled() || countryErrorMessage) {
            return;
        }

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }
        setIsSubmitting(true);

        const billTo = {
            email: formValues.email,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            phone: formValues.phone,
            postalCode: formValues.postalCode,
            address1: formValues.address1,
            address2: formValues.address2,
            country,
            city: formValues.city,
            state,
            companyName: formValues.companyName,
        };

        const account = {
            email: formValues.email,
            userName: formValues.userName,
            password: formValues.password,
            isSubscribed,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            billTo,
            properties: {
                unvalidatedAccountNumber: formValues.accountNumber,
                applyAsExistingCustomer: isExistingCustomer.toString(),
                Phone: formValues.phone,
            },
        };

        addAccount({
            ...account,
            onSuccess: onSuccessHandler,
            onError: (error: string) => {
                setIsSubmitting(false);
                xLoadingOverlay({ key: "clear", id: "Create_Account" });
                setErrorMessage(error);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        } as any);
    };

    const homeClickHandler = () => {
        history.push(homePageUrl);
    };

    if (isActivationEmailSent) {
        return (
            <StyledWrapper {...styles.activationSuccessWrapper}>
                <Typography as="h2" {...styles.activationSuccessTitleText}>
                    {translate("Check your email")}
                </Typography>
                <Typography {...styles.activationSuccessMessageText}>
                    {siteMessage("CreateNewAccountInfo_AccountActivation_SuccessMessage")}
                </Typography>
                <Button
                    {...styles.activationSuccessHomeButton}
                    onClick={homeClickHandler}
                    data-test-selector="createAccount_BackToHomePage"
                >
                    {translate("Home")}
                </Button>
            </StyledWrapper>
        );
    }

    const isSubmitEnabled = validateSubmitEnabled();

    return (
        // XNG-Changes: LoadingOverlay
        <LoadingOverlay loading={!countries || isSubmitting}>
            <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
                <GridContainer {...styles.mainGridContainer}>
                    <GridItem {...styles.userGridItem}>
                        <GridContainer {...styles.userGridContainer}>
                            <GridItem {...styles.requiredGridItem}>
                                <Typography {...styles.requiredText}>
                                    <Typography color="danger">* </Typography>
                                    {translate("Required")}
                                </Typography>
                                <StyledWrapper {...styles.applyAsExistingCustomerWrapper}>
                                    <Typography htmlFor="CreateNewAccountInfo_AccountNumber" forwardAs="label">
                                        {siteMessage("Apply as Existing Customer")}
                                    </Typography>
                                    <CheckboxGroup>
                                        <Checkbox
                                            onChange={(e, value) => setIsExistingCustomer(value)}
                                            checked={isExistingCustomer}
                                            data-test-selector="createAccount_IsExistingCustomer"
                                            hiddenIconText={translate("Is existing customer")}
                                        ></Checkbox>
                                    </CheckboxGroup>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.userNameGridItem}>
                                <TextField
                                    {...styles.userNameTextField}
                                    label={translate("First Name")}
                                    data-test-selector="createAccount_firstName"
                                    displayStarForRequired={true}
                                    labelPosition={isMobileScreen ? "top" : "left"}
                                    required
                                    value={formValues.firstName}
                                    name="firstName"
                                    {...{ formId }}
                                    onChange={handleInputChange}
                                />
                            </GridItem>
                            <GridItem {...styles.userNameGridItem}>
                                <TextField
                                    {...styles.userNameTextField}
                                    label={translate("Last Name")}
                                    labelPosition={isMobileScreen ? "top" : "left"}
                                    data-test-selector="createAccount_lastName"
                                    displayStarForRequired={true}
                                    required
                                    value={formValues.lastName}
                                    name="lastName"
                                    {...{ formId }}
                                    onChange={handleInputChange}
                                />
                            </GridItem>
                            <GridItem {...styles.emailGridItem}>
                                <TextField
                                    {...styles.emailTextField}
                                    type="email"
                                    label={translate("Email Address")}
                                    data-test-selector="createAccount_email"
                                    maxLength={256}
                                    required
                                    displayStarForRequired={true}
                                    labelPosition={isMobileScreen ? "top" : "left"}
                                    value={formValues.email}
                                    name="email"
                                    {...{ formId }}
                                    onChange={handleInputChange}
                                />
                            </GridItem>
                            <GridItem {...styles.phoneGridItem}>
                                <TextField
                                    {...styles.phoneTextField}
                                    label={translate("Phone Number")}
                                    displayStarForRequired={true}
                                    labelPosition={isMobileScreen ? "top" : "left"}
                                    required
                                    value={formValues.phoneNumber}
                                    name="phoneNumber"
                                    type="phone"
                                    {...{ formId }}
                                    onChange={handleInputChange}
                                />
                            </GridItem>

                            {!useEmailAsUserName && (
                                <GridItem {...styles.userNameGridItem}>
                                    <TextField
                                        {...styles.userNameTextField}
                                        label={translate("User Name")}
                                        data-test-selector="createAccount_userName"
                                        displayStarForRequired={true}
                                        labelPosition={isMobileScreen ? "top" : "left"}
                                        required
                                        maxLength={256}
                                        value={formValues.userName}
                                        name="userName"
                                        {...{ formId }}
                                        onChange={handleInputChange}
                                    />
                                </GridItem>
                            )}
                            {!requireActivateAccount && (
                                <>
                                    <GridItem {...styles.passwordGridItem}>
                                        <TextField
                                            id="password"
                                            {...styles.passwordTextField}
                                            label={translate("Password")}
                                            data-test-selector="createAccount_password"
                                            labelPosition={isMobileScreen ? "top" : "left"}
                                            required
                                            type="password"
                                            value={formValues.password}
                                            name="password"
                                            {...{ formId }}
                                            error={passwordErrorMessage}
                                            onChange={handleInputChange}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.confirmPasswordGridItem}>
                                        <TextField
                                            id="confirm-password"
                                            {...styles.confirmPasswordTextField}
                                            label={translate("Confirm Password")}
                                            data-test-selector="createAccount_confirmPassword"
                                            labelPosition={isMobileScreen ? "top" : "left"}
                                            required
                                            type="password"
                                            value={formValues.confirmPassword}
                                            name="confirmPassword"
                                            {...{ formId }}
                                            error={confirmPasswordErrorMessage}
                                            onChange={e => confirmPasswordChangeHandler(e.currentTarget.value)}
                                        />
                                    </GridItem>
                                </>
                            )}
                            <GridItem {...styles.accountNoGridItem}>
                                <TextField
                                    {...styles.accountNoTextField}
                                    label={translate("AWC Account Number")}
                                    data-test-selector="createAccount_accountNumber"
                                    labelPosition={isMobileScreen ? "top" : "left"}
                                    id="CreateNewAccountInfo_AccountNumber"
                                    value={formValues.accountNumber}
                                    name="accountNumber"
                                    {...{ formId }}
                                    onChange={handleInputChange}
                                />
                            </GridItem>
                            {isExistingCustomer && (
                                <GridItem width={12}>
                                    <GridContainer gap={12}>
                                        <GridItem width={12}>
                                            <Typography {...styles.billingTitle} variant="h3">
                                                {translate("Billing Information")}
                                            </Typography>
                                        </GridItem>
                                        <GridItem {...styles.sharedGridItem}>
                                            <TextField
                                                {...styles.sharedTextField}
                                                label={translate("Company Name")}
                                                value={formValues.companyName}
                                                displayStarForRequired={true}
                                                required={isExistingCustomer}
                                                {...{ formId }}
                                                name="companyName"
                                                onChange={handleInputChange}
                                            />
                                        </GridItem>

                                        <GridItem {...styles.sharedGridItem}>
                                            <TextField
                                                {...styles.sharedTextField}
                                                label={translate("Address 1")}
                                                displayStarForRequired={true}
                                                required={isExistingCustomer}
                                                onChange={handleInputChange}
                                                value={formValues.address1}
                                                name="address1"
                                                {...{ formId }}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.sharedGridItem}>
                                            <TextField
                                                {...styles.sharedTextField}
                                                label={translate("Address 2")}
                                                displayStarForRequired={true}
                                                onChange={handleInputChange}
                                                value={formValues.address2}
                                                name="address2"
                                            />
                                        </GridItem>
                                        <GridItem {...styles.sharedGridItem}>
                                            <TextField
                                                {...styles.sharedTextField}
                                                label={translate("City")}
                                                displayStarForRequired={true}
                                                required={isExistingCustomer}
                                                onChange={handleInputChange}
                                                value={formValues.city}
                                                name="city"
                                                {...{ formId }}
                                            />
                                        </GridItem>

                                        <GridItem {...styles.sharedGridItem}>
                                            <TextField
                                                {...styles.sharedTextField}
                                                label={translate("Postal Code")}
                                                displayStarForRequired={true}
                                                required={isExistingCustomer}
                                                onChange={handleInputChange}
                                                value={formValues.postalCode}
                                                name="postalCode"
                                                type={
                                                    country?.name === "Canada"
                                                        ? "postalCodeCA"
                                                        : country?.name?.toLowerCase() === "united states"
                                                        ? "postalCodeUS"
                                                        : "text"
                                                }
                                                maxLength={15}
                                                {...{ formId }}
                                            />
                                        </GridItem>

                                        {countries && countries.length > 1 && (
                                            <GridItem {...styles.sharedGridItem}>
                                                <Select
                                                    label={translate("Country")}
                                                    {...styles.countrySelect}
                                                    data-test-selector="country"
                                                    required={isExistingCustomer}
                                                    {...{ formId }}
                                                    name="country"
                                                    onChange={handleInputChange}
                                                    value={formValues.country}
                                                    error={countryErrorMessage}
                                                >
                                                    <option value="">{translate("Select Country")}</option>
                                                    {countries?.map((o: any) => (
                                                        <option key={o.abbreviation} value={o.abbreviation}>
                                                            {o.name}
                                                        </option>
                                                    ))}
                                                </Select>
                                            </GridItem>
                                        )}
                                        <GridItem {...styles.sharedGridItem}>
                                            <TextField
                                                {...styles.phoneTextField}
                                                label={translate("Phone")}
                                                type="phone"
                                                displayStarForRequired={true}
                                                onChange={handleInputChange}
                                                value={formValues.phone}
                                                name="phone"
                                                {...{ formId }}
                                            />
                                        </GridItem>
                                        {/* Display the dropdown only if states length > 1 */}
                                        {country && !!country.states?.length && country.states.length > 1 && (
                                            <GridItem {...styles.sharedGridItem}>
                                                <Select
                                                    label={translate("State")}
                                                    data-test-selector="state"
                                                    name="state"
                                                    onChange={handleInputChange}
                                                    value={formValues.state}
                                                    {...{ formId }}
                                                    required={isExistingCustomer && country.states?.length > 1}
                                                >
                                                    {country?.states?.map((o: any) => (
                                                        <option key={o.abbreviation} value={o.abbreviation}>
                                                            {o.name}
                                                        </option>
                                                    ))}
                                                </Select>
                                            </GridItem>
                                        )}
                                    </GridContainer>
                                </GridItem>
                            )}
                            <GridItem {...styles.subscriptionGridItem}>
                                <CheckboxGroup>
                                    <Checkbox
                                        onChange={(e, value) => setIsSubscribed(value)}
                                        checked={isSubscribed}
                                        data-test-selector="createAccount_Subscription"
                                    >
                                        {siteMessage("SignIn_Sign_Up_Newsletter")}
                                    </Checkbox>
                                </CheckboxGroup>
                            </GridItem>
                            {reCaptchaEnabledForCreateAccount && (
                                <GridItem {...styles.reCaptchaGridItem}>
                                    <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                                </GridItem>
                            )}
                            {errorMessage && (
                                <GridItem
                                    data-test-selector="createAccount_ErrorMessage"
                                    {...styles.submitErrorGridItem}
                                >
                                    <Notify type="danger">{errorMessage}</Notify>
                                </GridItem>
                            )}
                            <GridItem {...styles.submitGridItem}>
                                <Button
                                    {...styles.submitButton}
                                    type="submit"
                                    disabled={isSubmitting || (isSubmitted && !isSubmitEnabled)}
                                    data-test-selector="createAccount_createButton"
                                >
                                    {translate("Create Account")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    {requireActivateAccount ? (
                        <GridItem {...styles.activationInstructionGridItem}>
                            <Typography
                                {...styles.activationInstructionText}
                                data-test-selector="createAccount_AccountActivation_Instruction"
                            >
                                {siteMessage("CreateNewAccountInfo_AccountActivation_Instruction")}
                            </Typography>
                        </GridItem>
                    ) : (
                        <GridItem {...styles.requirementsGridItem}>
                            <StyledWrapper {...styles.requirementsWrapper}>
                                <Typography
                                    {...styles.requirementsTitle}
                                    data-test-selector="createAccount_passwordRequirements"
                                >
                                    {translate("Password Requirements")}
                                </Typography>
                                <GridContainer {...styles.passwordRequirementsGridContainer}>
                                    <GridItem {...styles.passwordRequirementsGridItem}>
                                        {translate(
                                            "Password must be at least {0} characters long",
                                            passwordMinimumLength.toString(),
                                        )}
                                    </GridItem>
                                    {passwordRequiresDigit && (
                                        <GridItem {...styles.passwordRequirementsGridItem}>
                                            {translate("Password must include at least one number")}
                                        </GridItem>
                                    )}
                                    {passwordRequiresLowercase && (
                                        <GridItem {...styles.passwordRequirementsGridItem}>
                                            {translate("Password must include at least one lowercase character")}
                                        </GridItem>
                                    )}
                                    {passwordRequiresUppercase && (
                                        <GridItem {...styles.passwordRequirementsGridItem}>
                                            {translate("Password must include at least one uppercase character")}
                                        </GridItem>
                                    )}
                                    {passwordRequiresSpecialCharacter && (
                                        <GridItem {...styles.passwordRequirementsGridItem}>
                                            {translate("Password must include at least one non alphanumeric character")}
                                        </GridItem>
                                    )}
                                </GridContainer>
                            </StyledWrapper>
                        </GridItem>
                    )}
                </GridContainer>
            </StyledForm>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withToaster(CreateAccount))),
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
    },
};

export default widgetModule;
